<script setup>
// Full-screen black QR scanner takeover (needs camera real estate).
// With a `submit` handler (live pass): a REAL camera scan (QrcodeStream, same
// stack as the Tickets QrModal) whose decoded value goes to the backend — the
// SERVER decides whether the code is valid; validation must never happen
// client-side. Without it (mock demo): "Tap to scan" reads for 1200ms, shows
// the found check, then emits success 700ms later.
// forceState="camera-denied" makes the scan resolve to the camera-permission
// recovery panel instead (powers the debug toggle).
// Port of `QrTakeover` in the source `_shared/CheckInSheet.tsx`.
import { ref, computed, onUnmounted } from 'vue'
import { X, ArrowLeft, Camera, Loader2, Check, AlertTriangle, Clock, CameraOff } from '@lucide/vue'
import { QrcodeStream } from 'vue-qrcode-reader'
import { computeCheckInAvailability } from './checkin'
import QrDetails from './QrDetails.vue'
import RecoveryPanel from './RecoveryPanel.vue'

const props = defineProps({
  spec: { type: Object, required: true },
  // When true the dismiss button renders as a back arrow and emits `back`
  // (plain qr method returns to the intro sheet); otherwise an X emitting `close`.
  canGoBack: { type: Boolean, default: false },
  // Debug hook: 'camera-denied' → the scan resolves to the failure state.
  forceState: { type: String, default: null },
  // Shown when the parent's Intercom fallback kicked in (chat unavailable).
  helpNotice: { type: Boolean, default: false },
  // Live validation: async ({ code }) => { ok, message } with the decoded QR
  // value. Presence of the handler also switches to the real camera scanner.
  submit: { type: Function, default: null },
})

const emit = defineEmits(['close', 'back', 'success', 'use-pin', 'help'])

const phase = ref('idle') // idle | scanning | found | camera-denied
// Server rejection of a scanned code (wrong/stale QR) — shown over the finder.
const scanError = ref('')
// Guards double-detects while a submission is in flight.
let submitting = false
const availability = computed(() => computeCheckInAvailability(props.spec.details))
const blocked = computed(() => !availability.value.ok)
const pinAvailable = computed(() => Boolean(props.spec.pin))

// OS-aware camera-permission recovery steps (RecoveryPanel adds the
// "Using Android?/Using iPhone?" toggle).
const CAMERA_STEPS = {
  ios: [
    "Tap the ᴬA button in Safari's address bar.",
    'Tap Website Settings.',
    'Set Camera to Allow, then come back and try again.',
  ],
  android: [
    'Tap the ⋮ menu → Settings → Site settings → Camera.',
    'Find this site and choose Allow.',
    'Come back and tap Try again.',
  ],
}

let scanTimer = null
let successTimer = null

onUnmounted(() => {
  clearTimeout(scanTimer)
  clearTimeout(successTimer)
})

const startScan = () => {
  scanError.value = ''
  phase.value = 'scanning'

  if (props.forceState === 'camera-denied') {
    scanTimer = setTimeout(() => {
      phase.value = 'camera-denied'
    }, 1200)
    return
  }

  // Live: QrcodeStream mounts with the 'scanning' phase and drives onDetect.
  if (props.submit) return

  // Demo: fake read, always succeeds.
  scanTimer = setTimeout(() => {
    phase.value = 'found'
    successTimer = setTimeout(() => emit('success'), 700)
  }, 1200)
}

// Real camera detect → the decoded value goes to the server for the verdict.
const onDetect = (detectedCodes) => {
  const rawValue = detectedCodes?.[0]?.rawValue
  if (!rawValue || submitting || phase.value !== 'scanning') return
  submitting = true
  scanError.value = ''
  props.submit({ code: rawValue })
    .then((result) => {
      if (result?.ok) {
        phase.value = 'found'
        successTimer = setTimeout(() => emit('success'), 700)
        return
      }
      scanError.value = result?.message || "That code didn't match this venue. Try the QR at the register."
    })
    .catch(() => {
      scanError.value = 'Something went wrong — try scanning again.'
    })
    .finally(() => {
      submitting = false
    })
}

// Camera-permission failure from the real stream → the existing recovery panel.
const onCameraError = () => {
  phase.value = 'camera-denied'
}
</script>

<template>
  <div class="cs-qr-takeover" data-overlay>
    <div class="cs-qr-takeover__column">
      <!-- Header -->
      <div class="cs-qr-takeover__header">
        <div>
          <div class="cs-qr-takeover__eyebrow">Scan to check in</div>
          <div class="cs-qr-takeover__venue">{{ spec.venueName }}</div>
        </div>
        <button
          id="checkin-qr-takeover-dismiss"
          type="button"
          class="cs-qr-takeover__dismiss gtm-click"
          :aria-label="canGoBack ? 'Back' : 'Close'"
          @click="$emit(canGoBack ? 'back' : 'close')"
        >
          <ArrowLeft v-if="canGoBack" :size="16" />
          <X v-else :size="16" />
        </button>
      </div>

      <!-- Camera-permission recovery: replaces the viewfinder + footer. -->
      <div v-if="phase === 'camera-denied'" class="cs-qr-takeover__stage cs-qr-takeover__stage--recovery" data-scrollable>
        <RecoveryPanel
          :icon="CameraOff"
          headline="We can't use your camera"
          :steps="CAMERA_STEPS"
          :pin-available="pinAvailable"
          bare
          id-prefix="checkin-qr-recovery"
          @retry="startScan"
          @use-pin="$emit('use-pin')"
          @help="$emit('help')"
        />
      </div>

      <!-- Viewfinder -->
      <div v-else class="cs-qr-takeover__stage">
        <div class="cs-qr-takeover__finder">
          <!-- Live camera (real scanner) while scanning; faux grain otherwise -->
          <QrcodeStream
            v-if="submit && phase === 'scanning' && forceState !== 'camera-denied'"
            class="cs-qr-takeover__stream"
            @detect="onDetect"
            @error="onCameraError"
          />
          <template v-else>
            <div class="cs-qr-takeover__camera" />
            <div class="cs-qr-takeover__grain" />
          </template>
          <!-- Corner brackets -->
          <div
            v-for="corner in ['tl', 'tr', 'bl', 'br']"
            :key="corner"
            class="cs-qr-takeover__corner"
            :class="[`cs-qr-takeover__corner--${corner}`, { 'cs-qr-takeover__corner--found': phase === 'found' }]"
          />
          <!-- Scan line (demo read only) -->
          <div v-if="phase === 'scanning' && !submit" class="cs-qr-takeover__scan-track">
            <div class="cs-qr-takeover__scan-line" />
          </div>
          <!-- Found check -->
          <div v-if="phase === 'found'" class="cs-qr-takeover__found">
            <div class="cs-qr-takeover__found-badge">
              <Check :size="32" :stroke-width="3" />
            </div>
          </div>
        </div>
        <div v-if="scanError" class="cs-qr-takeover__scan-error">{{ scanError }}</div>
      </div>

      <div v-if="helpNotice" class="cs-qr-takeover__help-notice">
        Support chat unavailable — call the venue or your pass provider.
      </div>

      <!-- Footer -->
      <div v-if="phase !== 'camera-denied'" class="cs-qr-takeover__footer">
        <p class="cs-qr-takeover__instructions">
          {{ spec.instructions ?? 'Point your camera at the QR code at the venue.' }}
        </p>
        <div class="cs-qr-takeover__spacer" />
        <QrDetails :details="spec.details" />
        <div v-if="blocked" class="cs-qr-takeover__blocked">
          <AlertTriangle class="cs-qr-takeover__blocked-icon" :size="14" />
          <div class="cs-qr-takeover__blocked-text">
            <div class="cs-qr-takeover__blocked-label">{{ availability.blockedLabel ?? 'Unavailable' }}</div>
            <div v-if="availability.reason" class="cs-qr-takeover__blocked-reason">{{ availability.reason }}</div>
            <div v-if="availability.nextAvailableLabel" class="cs-qr-takeover__blocked-next">
              <Clock :size="12" /> Next check-in {{ availability.nextAvailableLabel }}
            </div>
          </div>
        </div>
        <button
          id="checkin-qr-takeover-scan"
          type="button"
          class="cs-qr-takeover__cta gtm-click"
          :disabled="blocked || phase !== 'idle'"
          @click="startScan"
        >
          <template v-if="blocked">
            {{ availability.nextAvailableLabel
              ? `Next check-in ${availability.nextAvailableLabel}`
              : availability.blockedLabel ?? 'Unavailable' }}
          </template>
          <span v-else-if="phase === 'idle'" class="cs-qr-takeover__cta-inner">
            <Camera :size="14" /> Tap to scan
          </span>
          <span v-else-if="phase === 'scanning'" class="cs-qr-takeover__cta-inner">
            <Loader2 class="cs-qr-takeover__spinner" :size="14" /> Reading…
          </span>
          <span v-else class="cs-qr-takeover__cta-inner">
            <Check :size="14" /> Got it!
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `pointer-events-auto absolute inset-0 z-[180] flex flex-col bg-black
//          text-white animate-in fade-in duration-200`.
// v4: teleported to <body> (no PhoneFrame), so the takeover is fixed to the
// viewport and its black column is constrained to the app's max width — on a
// phone this is the full screen, exactly like the source's phone frame.
.cs-qr-takeover {
  position: fixed;
  inset: 0;
  // Above Vuetify overlays/chrome; source's z-[180] is phone-frame-local.
  z-index: 2410;
  display: flex;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  animation: cs-fade-in-kf 200ms both; // animate-in fade-in duration-200

  &__column {
    display: flex;
    width: 100%;
    max-width: var(--project-max-width, 480px);
    flex-direction: column;
    background: #000000;
    color: #ffffff;
  }

  // Source: `flex items-center justify-between px-5 pt-4 pb-2`
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px 8px; // pt-4 px-5 pb-2
  }

  // Source: `font-display text-[10px] font-bold uppercase tracking-[0.25em] text-white/60`
  &__eyebrow {
    font-family: var(--fr-font-display, inherit);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.25em;
    color: rgba(255, 255, 255, 0.6);
  }

  // Source: `font-serif-display text-base font-bold italic`
  &__venue {
    font-family: var(--fr-font-serif-display, Georgia, 'Times New Roman', serif);
    font-size: 16px;
    font-weight: 700;
    font-style: italic;
  }

  // Source: `grid h-8 w-8 place-items-center rounded-full bg-white/10`
  &__dismiss {
    @include cs-button-reset;
    display: grid;
    height: 32px; // h-8
    width: 32px; // w-8
    place-items: center;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  // Source: `relative flex flex-1 items-center justify-center px-6`
  &__stage {
    position: relative;
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24px; // px-6
    min-height: 0;

    // Recovery panel fills the stage as a scrollable column on short screens.
    // flex-start (not center) so overflowing content isn't clipped at the top.
    &--recovery {
      align-items: flex-start;
      overflow-y: auto;
      padding: 8px 24px 24px;

      > * {
        width: 100%;
      }
    }
  }

  // Intercom-unavailable fallback line (parent-driven).
  &__help-notice {
    padding: 4px 24px;
    text-align: center;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
  }

  // Source: `relative aspect-square w-[78%] max-w-[280px]`
  &__finder {
    position: relative;
    aspect-ratio: 1 / 1;
    width: 78%;
    max-width: 280px;
  }

  // Real camera stream (live mode) fills the finder like the faux camera does.
  &__stream {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    overflow: hidden;
  }

  // Server rejection of a scanned code.
  &__scan-error {
    margin-top: 12px;
    text-align: center;
    font-size: 12px;
    color: $cs-rose-500;
  }

  // Source: `absolute inset-0 rounded-2xl bg-gradient-to-br from-zinc-800
  //          via-zinc-900 to-black`
  &__camera {
    position: absolute;
    inset: 0;
    border-radius: 16px; // rounded-2xl
    background: linear-gradient(to bottom right, $cs-zinc-800, $cs-zinc-900, #000000);
  }

  // Source: `absolute inset-0 rounded-2xl opacity-40` + inline radial-gradient grain
  &__grain {
    position: absolute;
    inset: 0;
    border-radius: 16px;
    opacity: 0.4;
    background-image:
      radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.08) 1px, transparent 1.5px),
      radial-gradient(circle at 70% 80%, rgba(255, 255, 255, 0.08) 1px, transparent 1.5px);
    background-size: 10px 10px, 14px 14px;
  }

  // Source: `absolute h-7 w-7` + per-corner `border-t/r/b/l-2`, offsets
  // `top/right/bottom/left-3`; `border-white` → `border-emerald-400` when found
  &__corner {
    position: absolute;
    height: 28px; // h-7
    width: 28px; // w-7
    border-color: #ffffff;
    border-style: solid;
    border-width: 0;

    &--found {
      border-color: $cs-emerald-400;
    }

    &--tl {
      top: 12px;
      left: 12px;
      border-top-width: 2px;
      border-left-width: 2px;
    }

    &--tr {
      top: 12px;
      right: 12px;
      border-top-width: 2px;
      border-right-width: 2px;
    }

    &--bl {
      bottom: 12px;
      left: 12px;
      border-bottom-width: 2px;
      border-left-width: 2px;
    }

    &--br {
      bottom: 12px;
      right: 12px;
      border-bottom-width: 2px;
      border-right-width: 2px;
    }
  }

  // Source: `absolute inset-x-3 top-3 bottom-3 overflow-hidden`
  &__scan-track {
    position: absolute;
    left: 12px;
    right: 12px;
    top: 12px;
    bottom: 12px;
    overflow: hidden;
  }

  // Source: `h-px w-full animate-[scanline_1.6s_linear_infinite]
  //          bg-gradient-to-r from-transparent via-emerald-400 to-transparent
  //          shadow-[0_0_18px_4px_rgba(16,185,129,0.55)]`
  &__scan-line {
    height: 1px;
    width: 100%;
    background: linear-gradient(to right, transparent, $cs-emerald-400, transparent);
    box-shadow: 0 0 18px 4px rgba(16, 185, 129, 0.55);
    animation: cs-scanline-kf 1.6s linear infinite;
  }

  // Source: `absolute inset-0 grid place-items-center`
  &__found {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
  }

  // Source: `grid h-16 w-16 place-items-center rounded-full bg-emerald-500 text-white`
  &__found-badge {
    display: grid;
    height: 64px; // h-16
    width: 64px; // w-16
    place-items: center;
    border-radius: 9999px;
    background: $cs-emerald-500;
    color: #ffffff;
  }

  // Source: `px-6 pb-6 pt-2 text-center`
  &__footer {
    padding: 8px 24px 24px; // pt-2 px-6 pb-6
    text-align: center;
  }

  // Source: `text-[12px] text-white/70`
  &__instructions {
    margin: 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
  }

  // Source keeps an empty `mt-3` div between instructions and details.
  &__spacer {
    margin-top: 12px;
  }

  // Source: `mt-3 flex items-start gap-2 rounded-xl bg-rose-500/20 px-3 py-2
  //          text-left text-rose-100 ring-1 ring-rose-400/40`
  &__blocked {
    margin-top: 12px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-radius: 12px;
    background: rgba($cs-rose-500, 0.2);
    padding: 8px 12px;
    text-align: left;
    color: $cs-rose-100;
    box-shadow: 0 0 0 1px rgba($cs-rose-400, 0.4);
  }

  &__blocked-icon {
    margin-top: 2px; // mt-0.5
    flex-shrink: 0;
  }

  &__blocked-text {
    font-size: 11px;
    line-height: 1.375;
  }

  &__blocked-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__blocked-reason {
    opacity: 0.9;
  }

  &__blocked-next {
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    opacity: 0.9;
  }

  // Source: `mt-3 w-full rounded-full ${theme.primaryBtn} py-3 ...
  //          disabled:opacity-70`
  &__cta {
    @include cs-cta(0.7);
    margin-top: 12px; // mt-3
  }

  &__cta-inner {
    @include cs-cta-inner;
  }

  &__spinner {
    @include cs-spin;
  }
}

// Source keyframes `scanline` (verbatim)
@keyframes cs-scanline-kf {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(260px);
  }
  100% {
    transform: translateY(0);
  }
}

// tailwindcss-animate `animate-in fade-in duration-200`
@keyframes cs-fade-in-kf {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
